/**
 * Drag-to-move for fixed floating elements. Tap/click without drag runs onTap.
 */
window.initFloatingDrag = function initFloatingDrag(element, options = {}) {
  if (!element) return;

  const {
    storageKey = null,
    dragHandle = element,
    positionReference = element,
    onTap = null,
    ignoreSelector = null,
    margin = 10,
    tapThreshold = 10,
  } = options;

  const safeMargin = Math.max(0, margin);

  const loadPosition = () => {
    if (!storageKey) return false;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return false;
      const pos = JSON.parse(raw);
      if (typeof pos.x !== 'number' || typeof pos.y !== 'number') return false;
      applyPosition(pos.x, pos.y);
      return true;
    } catch {
      return false;
    }
  };

  const savePosition = (x, y) => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify({ x, y }));
    } catch {
      /* ignore */
    }
  };

  const clampPosition = (x, y) => {
    const rect = positionReference.getBoundingClientRect();
    const w = rect.width || positionReference.offsetWidth;
    const h = rect.height || positionReference.offsetHeight;
    const maxX = Math.max(safeMargin, window.innerWidth - w - safeMargin);
    const maxY = Math.max(safeMargin, window.innerHeight - h - safeMargin);
    return {
      x: Math.min(maxX, Math.max(safeMargin, x)),
      y: Math.min(maxY, Math.max(safeMargin, y)),
    };
  };

  const applyPosition = (x, y) => {
    const clamped = clampPosition(x, y);
    element.classList.add('is-free-position');
    element.style.setProperty('left', `${clamped.x}px`);
    element.style.setProperty('top', `${clamped.y}px`);
    element.style.setProperty('right', 'auto');
    element.style.setProperty('bottom', 'auto');
    element.dataset.dragX = String(clamped.x);
    element.dataset.dragY = String(clamped.y);
    element.dispatchEvent(new CustomEvent('floating-drag:move', { bubbles: true }));
    return clamped;
  };

  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let originX = 0;
  let originY = 0;
  let dragged = false;
  let pendingX = 0;
  let pendingY = 0;
  let moveFrame = null;

  const getCurrentOrigin = () => {
    if (element.classList.contains('is-free-position') && element.dataset.dragX && element.dataset.dragY) {
      return {
        x: parseFloat(element.dataset.dragX),
        y: parseFloat(element.dataset.dragY),
      };
    }
    const rect = positionReference.getBoundingClientRect();
    return { x: rect.left, y: rect.top };
  };

  const onPointerDown = (event) => {
    if (event.button !== 0) return;
    if (ignoreSelector && event.target.closest(ignoreSelector)) return;

    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    dragged = false;

    const origin = getCurrentOrigin();
    originX = origin.x;
    originY = origin.y;

    dragHandle.setPointerCapture?.(pointerId);
    event.preventDefault();
  };

  const queuePosition = (x, y) => {
    pendingX = x;
    pendingY = y;
    if (moveFrame !== null) return;

    moveFrame = requestAnimationFrame(() => {
      moveFrame = null;
      applyPosition(pendingX, pendingY);
    });
  };

  const onPointerMove = (event) => {
    if (pointerId === null || event.pointerId !== pointerId) return;

    const dx = event.clientX - startX;
    const dy = event.clientY - startY;

    if (!dragged && Math.hypot(dx, dy) < tapThreshold) return;

    if (!dragged) {
      dragged = true;
      element.classList.add('is-dragging');
    }

    queuePosition(originX + dx, originY + dy);
    event.preventDefault();
  };

  const finishPointer = (event) => {
    if (pointerId === null || event.pointerId !== pointerId) return;

    if (moveFrame !== null) {
      cancelAnimationFrame(moveFrame);
      moveFrame = null;
      applyPosition(pendingX, pendingY);
    }

    dragHandle.releasePointerCapture?.(pointerId);
    element.classList.remove('is-dragging');
    pointerId = null;

    if (dragged) {
      const x = parseFloat(element.dataset.dragX || '0');
      const y = parseFloat(element.dataset.dragY || '0');
      savePosition(x, y);
      return;
    }

    if (event.type !== 'pointercancel' && typeof onTap === 'function') onTap(event);
  };

  dragHandle.addEventListener('pointerdown', onPointerDown);
  dragHandle.addEventListener('pointermove', onPointerMove);
  dragHandle.addEventListener('pointerup', finishPointer);
  dragHandle.addEventListener('pointercancel', finishPointer);
  dragHandle.addEventListener('dragstart', (event) => event.preventDefault());

  window.addEventListener('resize', () => {
    if (!element.classList.contains('is-free-position')) return;
    const x = parseFloat(element.dataset.dragX || '0');
    const y = parseFloat(element.dataset.dragY || '0');
    applyPosition(x, y);
  });

  loadPosition();

  return { applyPosition, loadPosition };
};
